import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Menu, LogOut, LogIn, IdCard, UserCircle2, QrCode, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { vhrpImages, contactInfo } from "@/lib/site";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Layout() {
  const { user, loginWithGoogle, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);

  const links = [
    { to: "/", label: t("tabHome"), end: true },
    { to: "/membership", label: t("tabMembership") },
    { to: "/directory", label: t("dirTile") },
    { to: "/events", label: t("eventsTitle") },
    { to: "/videos", label: t("videosTitle") },
    { to: "/news", label: t("newsHeader") },
    { to: "/grievance", label: t("tabGrievance") },
    { to: "/about", label: t("quickAbout") },
    { to: "/contact", label: t("quickContact") },
  ];

  const linkClass = ({ isActive }) =>
    `whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${
      isActive ? "bg-brand-tint text-brand" : "text-ink-soft hover:text-brand"
    }`;

  return (
    <div className="flex min-h-screen flex-col bg-[#FDFBF7] font-sans text-ink">
      <header className="sticky top-0 z-50 border-b border-line bg-white/90 backdrop-blur">
        <div className="mx-auto flex h-16 w-full max-w-[1680px] items-center gap-3 px-4 lg:px-6">
          <Link to="/" className="flex shrink-0 items-center gap-2.5" data-testid="brand-logo">
            <img src={vhrpImages.logoMark} alt="VHRP" className="h-10 w-10 shrink-0 rounded-full" />
            <div className="hidden min-w-0 leading-tight sm:block">
              <div className="max-w-[180px] truncate text-sm font-extrabold text-brand-dark lg:max-w-[240px]">
                {t("orgName")}
              </div>
              <div className="max-w-[180px] truncate text-[10px] font-bold text-brand lg:max-w-[240px]">{t("motto")}</div>
            </div>
          </Link>

          <nav className="hidden min-w-0 flex-1 items-center justify-center gap-0.5 overflow-hidden 2xl:flex">
            {links.slice(0, 4).map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} className={linkClass} data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`}>
                {l.label}
              </NavLink>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-more" className="whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-semibold text-ink-soft transition-colors hover:text-brand">
                  {t("profMore")}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {[...links.slice(4), { to: "/verify", label: t("verifyTile") }].map((l) => (
                  <DropdownMenuItem key={l.to} onClick={() => navigate(l.to)} data-testid={`nav-more-${l.to.slice(1)}`} className="cursor-pointer font-semibold">
                    {l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          <div className="ml-auto flex shrink-0 items-center gap-2 2xl:ml-0">
            <LanguageSwitcher compact />
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="profile-menu-trigger"
                    className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border border-line bg-brand-tint"
                  >
                    {user.picture ? (
                      <img src={user.picture} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <UserCircle2 className="text-brand" size={22} />
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <div className="px-2 py-1.5">
                    <div className="truncate text-sm font-semibold">{user.name}</div>
                    <div className="truncate text-xs text-ink-muted">{user.email}</div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile" className="cursor-pointer gap-2">
                    <UserCircle2 size={16} /> {t("tabProfile")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/card")} data-testid="menu-card" className="cursor-pointer gap-2">
                    <IdCard size={16} /> {t("cardHeadTitle")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/verify")} data-testid="menu-verify" className="cursor-pointer gap-2">
                    <QrCode size={16} /> {t("verifyTile")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="cursor-pointer gap-2 text-[#C62828]">
                    <LogOut size={16} /> {t("profLogout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                data-testid="login-cta"
                onClick={loginWithGoogle}
                className="rounded-full bg-brand px-4 font-semibold hover:bg-brand-dark"
              >
                <span className="hidden max-w-[220px] truncate lg:inline">{t("loginGoogle")}</span>
                <LogIn size={18} className="lg:hidden" />
              </Button>
            )}

            {/* Mobile hamburger */}
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-trigger" className="flex h-9 w-9 items-center justify-center rounded-full border border-line 2xl:hidden">
                  <Menu size={20} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 overflow-y-auto">
                <SheetTitle className="sr-only">Menu</SheetTitle>
                <div className="mb-4 flex items-center gap-2">
                  <img src={vhrpImages.logoMark} alt="" className="h-9 w-9 rounded-full" />
                  <span className="text-sm font-extrabold text-brand-dark">{t("orgName")}</span>
                </div>
                <nav className="flex flex-col gap-1">
                  {[...links, { to: "/verify", label: t("verifyTile") }].map((l) => (
                    <SheetClose asChild key={l.to}>
                      <NavLink
                        to={l.to}
                        end={l.end}
                        className={({ isActive }) =>
                          `rounded-lg px-3 py-2.5 text-sm font-semibold ${
                            isActive ? "bg-brand-tint text-brand" : "text-ink-soft"
                          }`
                        }
                      >
                        {l.label}
                      </NavLink>
                    </SheetClose>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="mt-12 border-t border-line bg-[#29211D] text-white/80">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-10 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="mb-3 flex items-center gap-2">
              <img src={vhrpImages.logoMark} alt="" className="h-10 w-10 rounded-full" />
              <span className="font-extrabold text-white">{t("orgName")}</span>
            </div>
            <p className="text-sm leading-relaxed text-white/60">{t("motto")}</p>
          </div>
          <div>
            <div className="mb-3 text-xs font-bold uppercase tracking-widest text-brand-tint">
              {t("quickAbout")}
            </div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="hover:text-white">{t("aboutTitle")}</Link></li>
              <li><Link to="/membership" className="hover:text-white">{t("tabMembership")}</Link></li>
              <li><Link to="/directory" className="hover:text-white">{t("dirTile")}</Link></li>
              <li><Link to="/grievance" className="hover:text-white">{t("tabGrievance")}</Link></li>
            </ul>
          </div>
          <div>
            <div className="mb-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-brand-tint">
              <ShieldCheck size={14} /> Legal
            </div>
            <ul className="space-y-2 text-sm">
              <li><a href={`${process.env.REACT_APP_BACKEND_URL}/api/privacy`} target="_blank" rel="noreferrer" className="hover:text-white">{t("profPrivacy")}</a></li>
              <li><a href={`${process.env.REACT_APP_BACKEND_URL}/api/delete-account`} target="_blank" rel="noreferrer" className="hover:text-white">{t("profDelete")}</a></li>
              <li><Link to="/feedback" className="hover:text-white">{t("feedbackMenu")}</Link></li>
              <li><Link to="/verify" className="hover:text-white">{t("verifyTitle")}</Link></li>
            </ul>
          </div>
          <div>
            <div className="mb-3 text-xs font-bold uppercase tracking-widest text-brand-tint">
              {t("contactTitle")}
            </div>
            <ul className="space-y-2 text-sm text-white/70">
              <li><a href={contactInfo.landlineHref} className="hover:text-white">{contactInfo.landline}</a></li>
              <li className="text-xs text-white/50">{contactInfo.hours}</li>
              <li><a href={contactInfo.emailHref} className="hover:text-white">{contactInfo.email}</a></li>
              <li>{contactInfo.address}</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 py-4 text-center text-xs text-white/40">
          © {new Date().getFullYear()} {t("orgName")} · vhrp.in
        </div>
      </footer>
    </div>
  );
}
